import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CheckCircle2,
  Circle,
  ExternalLink,
  KeyRound,
  Sparkles,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth.jsx";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";

// ---------------------------------------------------------------------------
// Step catalog. Order matters — drives the stepper UI and "Next" button.
// `completedKey` (when set) maps to a boolean from the backend `completion`
// payload; if the backend says it's done, the step is rendered as completed
// regardless of skip state.
// ---------------------------------------------------------------------------
const STEPS = [
  {
    id: "welcome",
    completedKey: null,
    deepLink: null,
  },
  {
    id: "listen_port",
    completedKey: "has_primary_listen_port",
    deepLink: "/platform/system",
  },
  {
    id: "first_tenant",
    completedKey: "has_tenant",
    deepLink: "/platform/tenants",
  },
  {
    id: "first_trunk",
    completedKey: "has_trunk",
    deepLink: "/trunks",
  },
  {
    id: "first_route_and_user",
    // Both must be present for this step to read as "done"
    completedKey: ["has_route", "has_user"],
    deepLink: "/routing",
  },
];

function stepIsCompleted(step, completion) {
  if (!step.completedKey) return false;
  if (Array.isArray(step.completedKey)) {
    return step.completedKey.every((k) => Boolean(completion?.[k]));
  }
  return Boolean(completion?.[step.completedKey]);
}

export function useOnboardingState() {
  // Centralized fetch + mutation hook so the top-bar button and the wizard
  // share a single source of truth.
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = async () => {
    if (!user || user.scope !== "platform") {
      setLoading(false);
      return null;
    }
    try {
      const r = await api.get("/platform/onboarding/state");
      setData(r.data);
      setError(null);
      return r.data;
    } catch (e) {
      setError(e);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.scope]);

  const post = async (body) => {
    const r = await api.post("/platform/onboarding/state", body);
    setData(r.data);
    return r.data;
  };

  return { data, loading, error, refresh, post };
}

function StepperRail({ steps, activeIdx, completion, skipped }) {
  const { t } = useTranslation();
  return (
    <ol
      className="flex items-center gap-1 mb-4 overflow-x-auto"
      data-testid="onboarding-stepper"
    >
      {steps.map((s, i) => {
        const done = stepIsCompleted(s, completion);
        const isSkipped = skipped.includes(s.id);
        const isActive = i === activeIdx;
        return (
          <li
            key={s.id}
            className="flex items-center gap-1 min-w-0"
            data-testid={`onboarding-step-rail-${s.id}`}
          >
            <div
              className={`flex items-center gap-1.5 rounded-full px-2.5 py-1 border text-[11px] font-medium transition-colors ${
                isActive
                  ? "border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))]"
                  : done
                  ? "border-[hsl(var(--ok))]/40 bg-[hsl(var(--ok))]/10 text-[hsl(var(--ok))]"
                  : isSkipped
                  ? "border-border bg-muted/40 text-muted-foreground line-through"
                  : "border-border bg-background text-muted-foreground"
              }`}
            >
              {done ? (
                <CheckCircle2 className="h-3 w-3" />
              ) : (
                <Circle className="h-3 w-3" />
              )}
              <span className="whitespace-nowrap">
                {t(`onboarding.steps.${s.id}.title`)}
              </span>
            </div>
            {i < steps.length - 1 && (
              <span className="h-px w-3 bg-border shrink-0" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

function StepBody({ step, completion }) {
  const { t } = useTranslation();
  const done = stepIsCompleted(step, completion);
  return (
    <div className="space-y-3" data-testid={`onboarding-body-${step.id}`}>
      <p className="text-sm text-muted-foreground leading-relaxed">
        {t(`onboarding.steps.${step.id}.body`)}
      </p>
      <ul className="text-sm text-foreground space-y-1.5">
        {(t(`onboarding.steps.${step.id}.bullets`, { returnObjects: true }) || [])
          .map((b, i) => (
            <li key={i} className="flex items-start gap-2">
              <Check className="h-3.5 w-3.5 mt-0.5 text-[hsl(var(--accent))] shrink-0" />
              <span>{b}</span>
            </li>
          ))}
      </ul>
      {step.completedKey && (
        <div
          className={`text-xs rounded-md border px-3 py-2 ${
            done
              ? "border-[hsl(var(--ok))]/40 bg-[hsl(var(--ok))]/10 text-[hsl(var(--ok))]"
              : "border-border bg-muted/40 text-muted-foreground"
          }`}
          data-testid={`onboarding-status-${step.id}`}
        >
          {done
            ? t("onboarding.statusDone")
            : t("onboarding.statusPending")}
        </div>
      )}
    </div>
  );
}

export function OnboardingWizard({ open, onOpenChange, initialState, onChange }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState(initialState);
  const [activeIdx, setActiveIdx] = useState(0);
  const [busy, setBusy] = useState(false);

  // Sync incoming state when parent refetches.
  useEffect(() => {
    if (initialState) setState(initialState);
  }, [initialState]);

  // Auto-jump to first incomplete-non-skipped step on first open.
  useEffect(() => {
    if (!open || !state) return;
    const completion = state.completion || {};
    const skipped = state.state?.skipped_steps || [];
    const firstPending = STEPS.findIndex(
      (s) => !stepIsCompleted(s, completion) && !skipped.includes(s.id)
    );
    setActiveIdx(firstPending === -1 ? 0 : firstPending);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!user || user.scope !== "platform" || !state) return null;

  const completion = state.completion || {};
  const skipped = state.state?.skipped_steps || [];
  const step = STEPS[activeIdx];
  const isLastStep = activeIdx === STEPS.length - 1;
  const completedCount = STEPS.filter((s) => stepIsCompleted(s, completion))
    .length;
  const progressPct = Math.round((completedCount / STEPS.length) * 100);

  const sendAction = async (body, opts = {}) => {
    setBusy(true);
    try {
      const r = await api.post("/platform/onboarding/state", body);
      setState(r.data);
      if (onChange) onChange(r.data);
      if (opts.toast) toast.success(opts.toast);
      return r.data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("onboarding.errorSave"));
    } finally {
      setBusy(false);
    }
  };

  const handleSkipStep = async () => {
    await sendAction({ action: "skip_step", step: step.id });
    if (!isLastStep) setActiveIdx(activeIdx + 1);
  };

  const handleDismiss = async () => {
    await sendAction({ action: "dismiss" });
    onOpenChange(false);
  };

  const handleComplete = async () => {
    await sendAction(
      { action: "complete" },
      { toast: t("onboarding.completeToast") }
    );
    onOpenChange(false);
  };

  const handleNext = () => {
    if (isLastStep) return handleComplete();
    setActiveIdx(activeIdx + 1);
  };

  const handlePrev = () => {
    if (activeIdx > 0) setActiveIdx(activeIdx - 1);
  };

  const handleTakeMeThere = async () => {
    if (!step.deepLink) return;
    // Persist that the wizard was open, then navigate. Wizard remains
    // dismissable — user can come back via the top-bar button.
    await sendAction({ action: "touch" });
    onOpenChange(false);
    navigate(step.deepLink);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-2xl"
        data-testid="onboarding-wizard"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[hsl(var(--accent))]" />
            <span>{t("onboarding.title")}</span>
          </DialogTitle>
          <p className="text-xs text-muted-foreground pt-1">
            {t("onboarding.subtitle")}
          </p>
        </DialogHeader>

        <div className="flex items-center gap-3 text-xs text-muted-foreground mb-2">
          <span className="font-mono">
            {completedCount}/{STEPS.length}
          </span>
          <Progress value={progressPct} className="h-1.5 flex-1" />
          {state.must_change_password && (
            <span className="inline-flex items-center gap-1 text-[hsl(var(--warn))]">
              <KeyRound className="h-3 w-3" />
              {t("onboarding.mustChangePassword")}
            </span>
          )}
        </div>

        <StepperRail
          steps={STEPS}
          activeIdx={activeIdx}
          completion={completion}
          skipped={skipped}
        />

        <div className="rounded-lg border border-border bg-card/40 p-4 mb-4">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-mono text-muted-foreground">
              {String(activeIdx + 1).padStart(2, "0")}
            </span>
            <h3 className="text-base font-semibold leading-none">
              {t(`onboarding.steps.${step.id}.title`)}
            </h3>
          </div>
          <StepBody step={step} completion={completion} />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            disabled={busy}
            data-testid="onboarding-dismiss"
          >
            <X className="h-3.5 w-3.5 mr-1" />
            {t("onboarding.dontShowAgain")}
          </Button>

          <div className="flex-1" />

          <Button
            variant="outline"
            size="sm"
            onClick={handlePrev}
            disabled={busy || activeIdx === 0}
            data-testid="onboarding-prev"
          >
            <ArrowLeft className="h-3.5 w-3.5 mr-1" />
            {t("onboarding.back")}
          </Button>

          {step.completedKey && !stepIsCompleted(step, completion) && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleSkipStep}
              disabled={busy}
              data-testid="onboarding-skip-step"
            >
              {t("onboarding.skipStep")}
            </Button>
          )}

          {step.deepLink && (
            <Button
              variant="default"
              size="sm"
              onClick={handleTakeMeThere}
              disabled={busy}
              data-testid="onboarding-take-me-there"
            >
              {t("onboarding.takeMeThere")}
              <ExternalLink className="h-3.5 w-3.5 ml-1" />
            </Button>
          )}

          <Button
            variant={isLastStep ? "default" : "secondary"}
            size="sm"
            onClick={handleNext}
            disabled={busy}
            data-testid="onboarding-next"
          >
            {isLastStep ? t("onboarding.finish") : t("onboarding.next")}
            {!isLastStep && <ArrowRight className="h-3.5 w-3.5 ml-1" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function SetupGuideButton() {
  // Top-bar button that:
  //  - Is hidden when the install is fully configured AND the wizard was
  //    explicitly dismissed (don't nag power users)
  //  - Always available via PlatformShell when there's still work to do
  //  - Opens the wizard on click and forces the dismissed flag back to false
  //    so the wizard can be reopened.
  const { t } = useTranslation();
  const { data, refresh, post } = useOnboardingState();
  const { legal } = useAuth();
  const [open, setOpen] = useState(false);

  // Hooks must be called unconditionally — compute visibility AFTER hooks.
  const fullyConfigured = Boolean(data?.fully_configured);
  const dismissed = Boolean(data?.state?.dismissed);
  const shouldShow = Boolean(data?.should_show);
  // Phase L — never open the wizard while the legal-consent modal is up.
  // Stacking two non-dismissable dialogs traps the user behind the wrong
  // overlay; defer the wizard until the user has accepted the latest ToS.
  const legalBlocking = !!(legal?.terms_outdated || legal?.privacy_outdated);

  // Auto-open on first load when backend says we should show the wizard.
  useEffect(() => {
    if (legalBlocking) {
      // Make sure any previously-opened wizard closes when a re-consent
      // requirement appears (e.g. after a token refresh).
      if (open) setOpen(false);
      return;
    }
    if (shouldShow && !open) {
      const tm = setTimeout(() => setOpen(true), 200);
      return () => clearTimeout(tm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldShow, legalBlocking]);

  if (!data) return null;
  const hidden = fullyConfigured && dismissed;
  if (hidden) return null;

  const click = async () => {
    if (dismissed) {
      try {
        await post({ action: "reopen" });
      } catch {
        // ignore — open anyway from current state
      }
    }
    setOpen(true);
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        className="hidden sm:inline-flex"
        onClick={click}
        data-testid="onboarding-open-btn"
        title={t("onboarding.openTitle")}
      >
        <Sparkles className="h-3.5 w-3.5 mr-1.5 text-[hsl(var(--accent))]" />
        {t("onboarding.openLabel")}
        {!fullyConfigured && (
          <span
            className="ml-1.5 inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(var(--accent))] pulse-live"
            aria-hidden
          />
        )}
      </Button>
      <OnboardingWizard
        open={open}
        onOpenChange={setOpen}
        initialState={data}
        onChange={() => {
          // refresh hook state to keep the rail dot accurate
          refresh();
        }}
      />
    </>
  );
}

export default OnboardingWizard;
